/**
 * Desktop and web notifications for the realtime `notification` event (contracts NotificationEvent). The server
 * already chose who gets one, with the push rules (DMs, @-mentions, @here/@channel, followed-thread replies, huddle
 * rings; mute, notify = none); this decides whether to show it on this device: never when notifications are off here,
 * never for my own message, never while Do not disturb is on (logic/dnd), and not for the conversation I'm looking at
 * in a focused window.
 */
import type { Channel, NotificationEvent } from "@bundle/contracts";
import { parseUrl } from "./shell.ts";

/** A conversation on screen: a channel or DM (`threadRootId` absent) or one thread. */
export type OpenConversation = { channelId: string; threadRootId?: string };

export type NotifyContext = {
  meId: string;
  /** Notifications are on for this device (the person enabled them and the OS allows them). */
  enabled: boolean;
  /** This window is focused and visible. */
  focused: boolean;
  /** The conversations in the window's visible panes. */
  open: OpenConversation[];
  /** Do not disturb is on right now (logic/dnd `isPaused`): no banners. */
  paused?: boolean;
};

export type NotifyDecision =
  | { show: false; reason: "disabled" | "own" | "visible" | "paused" }
  /** `tag`: replaces an earlier notification with the same tag. `group`: notifications of one conversation. */
  | { show: true; tag: string; group: string };

/** Whether this notification event is on screen right now: its thread (a reply) or its conversation (otherwise). */
export function isOnScreen(event: NotificationEvent, open: OpenConversation[]): boolean {
  return open.some((o) => o.channelId === event.channelId && o.threadRootId === event.threadRootId);
}

export function notifyDecision(event: NotificationEvent, ctx: NotifyContext): NotifyDecision {
  if (!ctx.enabled) return { show: false, reason: "disabled" };
  if (event.userId === ctx.meId) return { show: false, reason: "own" };
  if (ctx.paused === true) return { show: false, reason: "paused" };
  if (ctx.focused && isOnScreen(event, ctx.open)) return { show: false, reason: "visible" };
  return { show: true, tag: event.collapseKey, group: event.channelId };
}

/**
 * The conversation a pane's URL shows, if any. `dmChannels`: my 1:1 DM channel per other person (DM routes name the
 * person, not the channel).
 */
export function openConversation(url: string, dmChannels: Record<string, string>): OpenConversation | undefined {
  const route = parseUrl(url);
  switch (route?.type) {
    case "channel":
      return { channelId: route.channelId };
    case "thread":
      return { channelId: route.channelId, threadRootId: route.rootId };
    case "dm": {
      const channelId = dmChannels[route.userId];
      return channelId === undefined ? undefined : { channelId };
    }
    default:
      return undefined;
  }
}

/** My 1:1 DM channel per other person (for `openConversation`). */
export function dmChannelsByPerson(channels: Record<string, Channel>, meId: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const id of Object.keys(channels).toSorted()) {
    const c = channels[id]!;
    if (c.kind !== "dm" || c.dmUserIds === undefined) continue;
    const others = c.dmUserIds.filter((u) => u !== meId);
    if (others.length === 1 && out[others[0]!] === undefined) out[others[0]!] = c.id;
  }
  return out;
}

// ---- Notification sound ----

/**
 * The notification sounds every app ships (scripts/sounds: knock, pop and pluck synthesized by generate.ts, chime and
 * ding recorded and made by import.ts; web/desktop play `/sounds/<name>.wav`, Android bundles the same files under the
 * same names). The first is the default.
 */
export const NOTIFICATION_SOUNDS = ["knock", "pop", "chime", "ding", "pluck"] as const;
export type NotificationSound = (typeof NOTIFICATION_SOUNDS)[number];
/** This device's sound: on/off, and which one (kept while off, so turning it back on restores the pick). */
export type SoundSetting = { on: boolean; sound: NotificationSound };
export const DEFAULT_SOUND: NotificationSound = "knock";
export const DEFAULT_SOUND_SETTING: SoundSetting = { on: true, sound: DEFAULT_SOUND };
/** A burst of notifications rings once: at most one sound per this many ms (across this device's windows and tabs). */
export const SOUND_THROTTLE_MS = 2000;

export type SoundContext = {
  meId: string;
  setting: SoundSetting;
  /** This window is focused and visible. */
  focused: boolean;
  /** The conversations in the window's visible panes. */
  open: OpenConversation[];
  now: number;
  /** When this device last played a notification sound (ms), if ever. */
  lastPlayedAt?: number;
  /** Do not disturb is on right now (logic/dnd `isPaused`): silent. */
  paused?: boolean;
};

export type SoundDecision = { play: false; reason: "off" | "own" | "paused" | "visible" | "throttled" } | { play: true; sound: NotificationSound };

/**
 * Whether a realtime `notification` event rings on this device. Slack's rules: the server already left out muted
 * conversations and notify = none (the same people a push goes to); never my own message; not the conversation I'm
 * looking at in a focused window (another conversation, a background window or a hidden tab does ring); and a burst
 * rings once. Independent of whether this device shows OS notifications (a sound with the app open is still useful).
 */
export function soundDecision(event: NotificationEvent, ctx: SoundContext): SoundDecision {
  if (!ctx.setting.on) return { play: false, reason: "off" };
  if (event.userId === ctx.meId) return { play: false, reason: "own" };
  if (ctx.paused === true) return { play: false, reason: "paused" };
  if (ctx.focused && isOnScreen(event, ctx.open)) return { play: false, reason: "visible" };
  if (ctx.lastPlayedAt !== undefined && ctx.now >= ctx.lastPlayedAt && ctx.now - ctx.lastPlayedAt < SOUND_THROTTLE_MS) return { play: false, reason: "throttled" };
  return { play: true, sound: ctx.setting.sound };
}

/** A stored sound setting (any JSON, from storage or another version) → a valid one; unknown parts fall back to the default. */
export function parseSoundSetting(raw: unknown): SoundSetting {
  if (typeof raw !== "object" || raw === null) return DEFAULT_SOUND_SETTING;
  const r = raw as { on?: unknown; sound?: unknown };
  const sound = NOTIFICATION_SOUNDS.find((s) => s === r.sound) ?? DEFAULT_SOUND;
  return { on: typeof r.on === "boolean" ? r.on : true, sound };
}
